import React from "react";
import { Box, Paper, SxProps, Theme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { setToastMessage } from "../../store/slices/app/appSlice";

type CopyablePanelProps = {
  /** Text copied to the clipboard. Empty/null → the panel is not copyable. */
  value?: string | number | null;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

/** Copies text to the clipboard and confirms it with the usual toast. */
export const useCopyToClipboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        setToastMessage({
          message: t("common.copied", "Copied to clipboard!"),
          severity: "success",
        })
      );
    } catch (error) {
      console.error("Clipboard write failed", error);
      dispatch(
        setToastMessage({
          message: t("common.copyFailed", "Couldn't copy to clipboard"),
          severity: "error",
        })
      );
    }
  };
};

/**
 * Panel for a single piece of data. On hover it shows a copy icon on the
 * right, and clicking anywhere on it (icon included) copies `value` to the
 * clipboard and confirms it with the usual toast.
 */
const CopyablePanel: React.FC<CopyablePanelProps> = ({
  value,
  children,
  sx,
}) => {
  const { t } = useTranslation();
  const copy = useCopyToClipboard();
  const text = value === null || value === undefined ? "" : String(value);
  const copyable = text.trim().length > 0;

  const handleCopy = () => copy(text);

  return (
    <Paper
      elevation={0}
      {...(copyable && {
        role: "button",
        tabIndex: 0,
        title: t("common.copyToClipboard", "Copy to clipboard"),
        onClick: handleCopy,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCopy();
          }
        },
      })}
      sx={[
        {
          position: "relative",
          ...(copyable && {
            cursor: "pointer",
            "&:hover .copy-icon, &:focus-visible .copy-icon": { opacity: 1 },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
        // After the caller's sx so a `p` there can't undo it: keeps the text
        // clear of the icon.
        copyable && { pr: 5 },
      ]}
    >
      {children}
      {copyable && (
        <Box
          className="copy-icon"
          aria-hidden
          sx={{
            position: "absolute",
            top: "50%",
            right: 12,
            transform: "translateY(-50%)",
            display: "flex",
            color: "text.secondary",
            opacity: 0,
            transition: "opacity 0.15s",
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
    </Paper>
  );
};

export default CopyablePanel;
